import type { Recipe } from '@awwyeah/shared';
import { Link } from 'react-router-dom';
import { useRecipePhotoSource } from './RecipePhoto';

export interface RecipeListRowProps {
  recipe: Recipe;
}

/**
 * Horizontal row for the recipe box's list view (owner ask, 2026-08-04: "text nice and wide
 * and the image for the meal in the background on the right fading out to the left. Or no
 * image at all and just max room for the text.").
 *
 * The fade is a real CSS `mask-image` on the `<img>` itself — not a gradient overlay div —
 * so it reveals whatever the row's own `bg-surface` happens to be in the active theme with
 * zero colour math and no risk of a mismatched seam between an overlay and the photo. That
 * mirrors `placeholderArt.ts`'s "let the theme do the work" approach without needing any
 * theme token in the mask itself (a mask is alpha-only, so there's nothing to get wrong
 * across light/dark/cosmos).
 *
 * When there's no photo (WS-20's placeholder-art gradients are deliberately *not* reused
 * here — the owner explicitly offered "no image at all" as the alternative, not a
 * placeholder), the row is just the text stack at full width.
 */
export function RecipeListRow({ recipe }: RecipeListRowProps): JSX.Element {
  const photoSrc = useRecipePhotoSource(recipe.photoRef, 'thumb');
  const subtitle =
    [recipe.tags[0], recipe.servings ? `serves ${recipe.servings}` : undefined]
      .filter(Boolean)
      .join(' · ') || 'No tags yet';

  return (
    <Link
      to={`/recipes/${recipe.id}`}
      data-testid="recipe-list-row"
      className="relative flex min-h-[96px] items-center overflow-hidden rounded-md border border-line bg-surface ease-app hover:border-line-strong"
    >
      {photoSrc && (
        <img
          src={photoSrc}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          data-testid="recipe-list-row-photo"
          // Owner tune 2026-08-04: the photo keeps to the right ~40% of the row (was
          // full-bleed) so the text side reads clear, fading out across its own left half.
          className="pointer-events-none absolute inset-y-0 right-0 w-2/5 object-cover"
          style={{
            // Taller rows + a crop biased toward the dish (food shots put the subject
            // mid-frame, not at the top) — owner tune 2026-08-04.
            objectPosition: 'center 42%',
            maskImage: 'linear-gradient(to left, black 0%, black 45%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to left, black 0%, black 45%, transparent 100%)',
          }}
        />
      )}
      <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-0.5 px-4 py-3">
        <span
          className="truncate text-[16px] font-semibold text-fg"
          // A halo of the row's own surface colour — keeps the title crisp where it meets
          // the photo, in every theme, without hardcoding light/dark shadow colours.
          style={{ textShadow: '0 1px 2px var(--app-surface), 0 0 8px var(--app-surface)' }}
        >
          {recipe.title}
        </span>
        <span className="truncate text-[13px] text-fg-3">{subtitle}</span>
      </div>
    </Link>
  );
}
